import { ref, type Ref } from 'vue';
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { useSimpleTextTooltip } from './simpleTextTooltip.uses';

export interface UseEllipsisTooltipReturn {
  tooltipMouseEvent: Ref<MouseEvent | null>;
  onMouseEventOnText: (e: MouseEvent) => void;
}

/**
 * 텍스트가 말줄임(scrollWidth > clientWidth)된 경우에만 SimpleTextTooltip 을 띄우는 헬퍼.
 *
 * - `mouseenter` 시점에 ellipsis 여부를 1회 판정한다.
 * - 그 외 이벤트(`mouseleave` 등)는 훅에 위임해 자동 닫힘 처리한다.
 * - 툴팁이 떠 있는 동안 ellipsis 상태/위치가 변할 수 있는 리사이즈가 발생하면 닫는다:
 *   - window resize
 *   - 트리거 엘리먼트 자체 리사이즈(GNB 사이드바 토글 등)
 *
 * @param predicate - 추가 게이트. 반환값이 false 면 툴팁을 열지 않는다. 닫힘/정리 동작은 게이트와 무관하게 항상 수행된다.
 */
export const useEllipsisTooltip = (predicate?: () => boolean): UseEllipsisTooltipReturn => {
  const tooltipMouseEvent = ref<MouseEvent | null>(null);
  const triggerEl = ref<HTMLElement | null>(null);
  let skipNextResize = false;

  const { onMouseEventInTarget } = useSimpleTextTooltip(tooltipMouseEvent);

  const closeTooltip = () => {
    if (tooltipMouseEvent.value !== null) tooltipMouseEvent.value = null;
  };

  const onMouseEventOnText = (e: MouseEvent) => {
    if (e.type === 'mouseenter') {
      if (predicate && !predicate()) return;
      const target = e.target as HTMLElement;
      if (target.scrollWidth <= target.clientWidth) return;
      if (triggerEl.value !== target) {
        skipNextResize = true;
        triggerEl.value = target;
      }
    } else {
      triggerEl.value = null;
    }
    onMouseEventInTarget(e);
  };

  useEventListener(window, 'resize', closeTooltip);

  // 리사이즈로 닫힌 후에도 트리거 위에서 마우스를 움직이면 다시 표시한다.
  useEventListener(triggerEl, 'mousemove', (e: MouseEvent) => {
    if (tooltipMouseEvent.value !== null) return;
    if (predicate && !predicate()) return;
    const el = triggerEl.value;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth) return;
    tooltipMouseEvent.value = e;
  });

  // observe() 직후 fire 되는 초기 콜백은 무시한다.
  useResizeObserver(triggerEl, () => {
    if (skipNextResize) {
      skipNextResize = false;
      return;
    }
    closeTooltip();
  });

  return {
    tooltipMouseEvent,
    onMouseEventOnText,
  };
};
